import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const inputCls = "h-11 w-full rounded-md border border-input px-3 text-sm";

export const ClientAuthDialog = ({ open, onOpenChange, onSuccess }) => {
  const { login, registerClient } = useAuth();
  const [mode, setMode] = useState("register");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = mode === "register" ? await registerClient(form) : await login(form.email, form.password);
      toast.success(mode === "register" ? "Bienvenue dans la communauté link & rent !" : "Connexion réussie");
      onSuccess?.(u);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="client-auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WhatsAppIcon className="h-6 w-6 text-[#25D366]" /> Contacter le loueur</DialogTitle>
          <DialogDescription>
            {mode === "register" ? "Créez votre accès gratuit pour rejoindre la communauté et contacter directement les loueurs sur WhatsApp." : "Connectez-vous pour contacter le loueur."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex rounded-lg bg-slate-100 p-1 text-sm font-medium">
          <button type="button" data-testid="client-auth-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-md py-1.5 ${mode === "register" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Créer mon accès</button>
          <button type="button" data-testid="client-auth-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-md py-1.5 ${mode === "login" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>J'ai déjà un compte</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && <input required placeholder="Nom complet" data-testid="client-name" value={form.full_name} onChange={set("full_name")} className={inputCls} />}
          <input required type="email" placeholder="Email" data-testid="client-email" value={form.email} onChange={set("email")} className={inputCls} />
          {mode === "register" && <input required placeholder="Téléphone" data-testid="client-phone" value={form.phone} onChange={set("phone")} className={inputCls} />}
          <input required type="password" minLength={6} placeholder="Mot de passe (min. 6 caractères)" data-testid="client-password" value={form.password} onChange={set("password")} className={inputCls} />
          {error && <p className="text-sm text-red-600" data-testid="client-auth-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="client-auth-submit" className="w-full h-11 text-white font-semibold" style={{ background: "#25D366" }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><WhatsAppIcon className="h-5 w-5 mr-2" /> {mode === "register" ? "Créer mon accès et contacter" : "Se connecter et contacter"}</>}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><Users className="h-3.5 w-3.5" /> Accès gratuit · Sans engagement</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const WhatsAppContact = ({ vehicle, dates, variant = "icon" }) => {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!vehicle.agency?.has_whatsapp) return null;

  const openWhatsApp = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicles/${vehicle.id}/contact`);
      const text = `Bonjour, je suis intéressé(e) par votre ${vehicle.brand} ${vehicle.model} (${vehicle.price_per_day} MAD/jour) vue sur link & rent.${dates?.start && dates?.end ? ` Dates souhaitées : du ${dates.start} au ${dates.end}.` : ""}`;
      window.open(`https://wa.me/${data.whatsapp}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (user) openWhatsApp(); else setAuthOpen(true);
  };

  return (
    <>
      {variant === "icon" ? (
        <button type="button" onClick={handleClick} disabled={loading} title="Contacter le propriétaire sur WhatsApp" data-testid={`whatsapp-icon-btn-${vehicle.id}`}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition-transform hover:scale-110" style={{ background: "#25D366" }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <WhatsAppIcon className="h-5 w-5" />}
        </button>
      ) : (
        <button type="button" onClick={handleClick} disabled={loading} data-testid="whatsapp-contact-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-md h-12 text-base font-semibold text-white transition-colors hover:brightness-110" style={{ background: "#25D366" }}>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <WhatsAppIcon className="h-6 w-6" />} Contacter le propriétaire
        </button>
      )}
      <ClientAuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => { setAuthOpen(false); openWhatsApp(); }} />
    </>
  );
};
